import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import IndustrialProduct from "./components/IndustrialProduct";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";

import "./Lavadora.css";

/* ============================================================
   API
============================================================ */

const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* ============================================================
   OBTENER CATÁLOGO DE LAVADORAS
============================================================ */

const obtenerCatalogoLavadoras = async () => {
  const respuesta = await axios.get(API_URL, {
    timeout: 45000,
  });

  return respuesta.data || {};
};

/* ============================================================
   COMPONENTE
============================================================ */

function Lavadora() {
  /* ============================================================
     FILTROS
  ============================================================ */

  const [filtroSeleccionado, setFiltroSeleccionado] = useState("todos");

  const [filtroBusqueda, setFiltroBusqueda] = useState("");

  /* ============================================================
     PAGINACIÓN
  ============================================================ */

  const [paginaActual, setPaginaActual] = useState(1);

  const productosPorPagina = 16;

  /* ============================================================
     TANSTACK QUERY + INDEXEDDB
  ============================================================ */

  const {
    data: catalogo = {},
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["catalogo-lavadoras"],
    queryFn: obtenerCatalogoLavadoras,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (isError) {
    console.error("Error al obtener productos de lavadoras:", error);
  }

  /* ============================================================
     SCROLL AL CAMBIAR PÁGINA
  ============================================================ */

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [paginaActual]);

  /* ============================================================
     NORMALIZAR TEXTO
  ============================================================ */

  const normalizarTexto = (texto = "") => {
    return String(texto)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  };

  /* ============================================================
     BÚSQUEDA
  ============================================================ */

  const coincideBusqueda = (item) => {
    if (!filtroBusqueda.trim()) {
      return true;
    }

    const palabras = normalizarTexto(filtroBusqueda)
      .split(/\s+/)
      .filter(Boolean);

    const textoProducto = normalizarTexto(
      `${item?.nombre || ""} ${item?.marca || ""} ${
        item?.modelo || ""
      } ${item?.codigoProducto || ""} ${item?.descripcion || ""}`,
    );

    return palabras.every((palabra) => textoProducto.includes(palabra));
  };

  /* ============================================================
     CATEGORÍAS
  ============================================================ */

  const categorias = useMemo(
    () => ({
      actuator: catalogo.actuators || [],
      manguera: catalogo.mangueras || [],
      selenoidelav: catalogo.selenoidelavs || [],
      motorsecadora: catalogo.motorsecadoras || [],
      motorlavadora: catalogo.motorlavadoras || [],
    }),
    [catalogo],
  );

  /* ============================================================
     TODOS LOS PRODUCTOS
  ============================================================ */

  const todosLosProductos = useMemo(() => {
    return Object.values(categorias).flat();
  }, [categorias]);

  /* ============================================================
     PRODUCTOS FILTRADOS
  ============================================================ */

  const productosFiltrados = useMemo(() => {
    const resultado = {};

    Object.entries(categorias).forEach(([categoria, productos]) => {
      resultado[categoria] = productos.filter(coincideBusqueda);
    });

    resultado.todos = todosLosProductos.filter(coincideBusqueda);

    return resultado;
  }, [categorias, todosLosProductos, filtroBusqueda]);

  /* ============================================================
     NOMBRES
  ============================================================ */

  const nombresLegibles = {
    todos: "Todos los productos",
    actuator: "Actuadores",
    manguera: "Mangueras",
    selenoidelav: "Electroválvulas",
    motorsecadora: "Motores de Secado",
    motorlavadora: "Motores de Lavado",
  };

  /* ============================================================
     TÍTULOS
  ============================================================ */

  const titulosCategoria = {
    todos: "Componentes para Lavadoras",
    actuator: "Actuadores",
    manguera: "Mangueras para Lavadoras",
    selenoidelav: "Electroválvulas",
    motorsecadora: "Motores de Secado",
    motorlavadora: "Motores de Lavado",
  };

  /* ============================================================
     ICONOS
  ============================================================ */

  const iconosCategoria = {
    todos: "bi-grid",
    actuator: "bi-gear-fill",
    manguera: "bi-bezier2",
    selenoidelav: "bi-droplet-fill",
    motorsecadora: "bi-fan",
    motorlavadora: "bi-arrow-repeat",
  };

  const obtenerTituloCategoria = () =>
    titulosCategoria[filtroSeleccionado] || titulosCategoria.todos;

  /* ============================================================
     RESULTADOS
  ============================================================ */

  const productosCategoria = productosFiltrados[filtroSeleccionado] || [];

  const totalProductos = todosLosProductos.length;

  const productosMostrados = productosCategoria.length;

  /* ============================================================
     PRODUCTOS DE LA PÁGINA ACTUAL
  ============================================================ */

  const productosEnPagina =
    filtroSeleccionado === "todos"
      ? productosCategoria.slice(
          (paginaActual - 1) * productosPorPagina,
          paginaActual * productosPorPagina,
        )
      : productosCategoria;

  /* ============================================================
     TOTAL DE PÁGINAS
  ============================================================ */

  const totalPaginas =
    filtroSeleccionado === "todos"
      ? Math.ceil(productosCategoria.length / productosPorPagina)
      : 1;

  /* ============================================================
     CAMBIAR CATEGORÍA
  ============================================================ */

  const seleccionarCategoria = (categoria) => {
    setFiltroSeleccionado(categoria);
    setFiltroBusqueda("");
    setPaginaActual(1);
  };

  /* ============================================================
     CAMBIAR PÁGINA
  ============================================================ */

  const cambiarPagina = (pagina) => {
    if (pagina < 1 || pagina > totalPaginas) {
      return;
    }

    setPaginaActual(pagina);
  };

  /* ============================================================
     VISTA
  ============================================================ */

  return (
    <div className="lavadora-page">
      <Navbar />

      <main>
        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="lavadora-hero">
          <div className="lavadora-container">
            <div className="lavadora-hero-card">
              <div>
                <span className="lavadora-kicker">CATÁLOGO G&amp;J FRÍO</span>

                <h1>{obtenerTituloCategoria()}</h1>

                <p>
                  Encuentra repuestos y componentes para lavadoras, secadoras y
                  sistemas de lavado de diferentes marcas.
                </p>
              </div>

              <div className="lavadora-hero-icon">
                <i className="bi bi-arrow-repeat"></i>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            FILTROS
        ===================================================== */}

        <section className="lavadora-toolbar-section">
          <div className="lavadora-container">
            <div className="lavadora-toolbar">
              <div className="lavadora-toolbar-left">
                <button
                  type="button"
                  className="lavadora-category-button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#lavadoraCategories"
                  aria-controls="lavadoraCategories"
                >
                  <i className="bi bi-grid-fill"></i>

                  <span>Categorías</span>

                  <i className="bi bi-chevron-right"></i>
                </button>

                <div className="lavadora-current-category">
                  <span>Categoría actual</span>

                  <strong>{nombresLegibles[filtroSeleccionado]}</strong>
                </div>
              </div>

              {/* CONTADOR */}

              <div className="lavadora-counter">
                <span>Mostrando</span>

                <strong>{!isLoading ? productosMostrados : 0}</strong>

                <span>de</span>

                <strong>{!isLoading ? totalProductos : 0}</strong>

                <span>{totalProductos === 1 ? "artículo" : "artículos"}</span>
              </div>
            </div>

            {/* =================================================
                BUSCADOR
            ================================================= */}

            <div className="lavadora-search-row">
              <div className="lavadora-search">
                <i className="bi bi-search"></i>

                <input
                  type="text"
                  placeholder="Buscar por nombre, marca, modelo o código..."
                  value={filtroBusqueda}
                  onChange={(e) => {
                    setFiltroBusqueda(e.target.value);
                    setPaginaActual(1);
                  }}
                />

                {filtroBusqueda && (
                  <button
                    type="button"
                    className="lavadora-search-clear"
                    onClick={() => {
                      setFiltroBusqueda("");
                      setPaginaActual(1);
                    }}
                    aria-label="Limpiar búsqueda"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            OFFCANVAS
        ===================================================== */}

        <div
          className="offcanvas offcanvas-start lavadora-offcanvas"
          data-bs-scroll="true"
          tabIndex="-1"
          id="lavadoraCategories"
          aria-labelledby="lavadoraCategoriesLabel"
        >
          <div className="offcanvas-header lavadora-offcanvas-header">
            <div>
              <span>CATÁLOGO</span>

              <h5 className="offcanvas-title" id="lavadoraCategoriesLabel">
                Subcategorías
              </h5>
            </div>

            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="offcanvas"
              aria-label="Cerrar"
            ></button>
          </div>

          <div className="offcanvas-body lavadora-offcanvas-body">
            <div className="lavadora-category-list">
              {Object.keys(nombresLegibles).map((categoria) => {
                const cantidad = productosFiltrados[categoria]?.length || 0;

                return (
                  <button
                    type="button"
                    key={categoria}
                    className={`lavadora-category-item ${
                      filtroSeleccionado === categoria ? "active" : ""
                    }`}
                    onClick={() => seleccionarCategoria(categoria)}
                    data-bs-dismiss="offcanvas"
                  >
                    <span className="lavadora-category-item-icon">
                      <i className={`bi ${iconosCategoria[categoria]}`}></i>
                    </span>

                    <span className="lavadora-category-item-text">
                      <strong>{nombresLegibles[categoria]}</strong>

                      <small>
                        {!isLoading ? cantidad : 0}{" "}
                        {cantidad === 1 ? "producto" : "productos"}
                      </small>
                    </span>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* =====================================================
            PRODUCTOS
        ===================================================== */}

        <section className="lavadora-products-section">
          <div className="lavadora-container">
            {/* =================================================
                GROWING SPINNER
            ================================================= */}

            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  minHeight: "100px",
                }}
              >
                <div
                  className="spinner-grow text-primary"
                  role="status"
                  style={{
                    width: "1.6rem",
                    height: "1.6rem",
                  }}
                >
                  <span className="visually-hidden">Cargando productos...</span>
                </div>
              </div>
            ) : productosEnPagina.length > 0 ? (
              /* ===============================================
                 PRODUCTOS
              =============================================== */

              <div className="lavadora-products-grid">
                {productosEnPagina.map((item) => (
                  <IndustrialProduct
                    key={item._id}
                    img={item.imagen}
                    title={item.nombre}
                    id={item._id}
                    details={[
                      {
                        label: "Marca",
                        value: item.marca,
                      },

                      ...(item.modelo
                        ? [
                            {
                              label: "Modelo",
                              value: item.modelo,
                            },
                          ]
                        : []),

                      {
                        label: "Código Producto",
                        value: item.codigoProducto,
                      },

                      {
                        label: "Descripción",
                        value: item.descripcion,
                      },
                    ]}
                  />
                ))}
              </div>
            ) : (
              /* ===============================================
                 SIN RESULTADOS
              =============================================== */

              <div className="lavadora-empty">
                <div className="lavadora-empty-icon">
                  <i className="bi bi-search"></i>
                </div>

                <h3>No encontramos productos</h3>

                <p>
                  No existen coincidencias para la búsqueda o categoría
                  seleccionada.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setFiltroBusqueda("");
                    setFiltroSeleccionado("todos");
                    setPaginaActual(1);
                  }}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                  Mostrar todos
                </button>
              </div>
            )}

            {/* =================================================
                PAGINACIÓN
            ================================================= */}

            {!isLoading &&
              filtroSeleccionado === "todos" &&
              totalPaginas > 1 && (
                <nav
                  className="lavadora-pagination-wrapper"
                  aria-label="Paginación de productos"
                >
                  {/* ANTERIOR */}

                  <button
                    type="button"
                    className="lavadora-pagination-arrow"
                    disabled={paginaActual === 1}
                    onClick={() => cambiarPagina(paginaActual - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>

                    <span>Anterior</span>
                  </button>

                  {/* NÚMEROS */}

                  <div className="lavadora-pagination-pages">
                    {Array.from(
                      {
                        length: totalPaginas,
                      },
                      (_, index) => index + 1,
                    ).map((pagina) => (
                      <button
                        type="button"
                        key={pagina}
                        className={`lavadora-page-button ${
                          paginaActual === pagina ? "active" : ""
                        }`}
                        onClick={() => cambiarPagina(pagina)}
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>

                  {/* SIGUIENTE */}

                  <button
                    type="button"
                    className="lavadora-pagination-arrow"
                    disabled={paginaActual === totalPaginas}
                    onClick={() => cambiarPagina(paginaActual + 1)}
                  >
                    <span>Siguiente</span>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                </nav>
              )}
          </div>
        </section>
      </main>

      <Footer />

      <WhatsAppButton />
    </div>
  );
}

export default Lavadora;
